import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link } from 'react-router-dom';
import axios from 'axios';

const GetApplyLeads = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch all applicants
  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://theagencybazaar.com/api/applicants');
      if (response.data.success) {
        setApplicants(response.data.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching applicants:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  // Handle delete applicant
  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}'s application?`)) {
      try {
        const response = await axios.delete(`https://theagencybazaar.com/api/applicant/${id}`);
        if (response.data.success) {
          alert('Application deleted successfully');
          fetchApplicants();
        } else {
          alert('Failed to delete');
        }
      } catch (err) {
        console.error('Error deleting:', err);
        alert('Error deleting application');
      }
    }
  };

  // Handle status update
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await axios.put(`https://theagencybazaar.com/api/applicant/${id}/status`, {
        status: newStatus
      });
      if (response.data.success) {
        alert('Status updated successfully');
        fetchApplicants();
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error updating status');
    }
  };

  // Handle view details
  const handleViewDetails = (applicant) => {
    setSelectedApplicant(applicant);
    setShowModal(true);
  };

  // Handle download CV - FIXED
  const handleDownloadCV = (resumePath, fileName) => {
    try {
      let cleanPath = resumePath.replace(/\\/g, '/');
      
      if (cleanPath.includes('/Uploads/')) {
        cleanPath = cleanPath.substring(cleanPath.indexOf('/Uploads/'));
      } else if (cleanPath.includes('Uploads\\')) {
        cleanPath = '/Uploads/' + cleanPath.split('Uploads\\')[1];
      } else if (cleanPath.includes('resume-')) {
        cleanPath = '/Uploads/' + cleanPath.split('\\').pop().split('/').pop();
      }
      
      const fileUrl = `https://theagencybazaar.com${cleanPath}`;
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading file. Please check if file exists.');
    }
  };

  // Filter applicants based on search and status
  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = searchTerm === '' || 
      applicant.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.Email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.Contact.includes(searchTerm);
    
    const matchesStatus = statusFilter === '' || applicant.Status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status badge color
  const getStatusBadge = (status) => {
    switch(status) {
      case 'Pending':
        return <span className="badge bg-warning text-dark" style={{ fontSize: '11px' }}>Pending</span>;
      case 'Approved':
        return <span className="badge bg-success" style={{ fontSize: '11px' }}>Approved</span>;
      case 'Rejected':
        return <span className="badge bg-danger" style={{ fontSize: '11px' }}>Rejected</span>;
      case 'Interview Scheduled':
        return <span className="badge bg-info" style={{ fontSize: '11px' }}>Interview Scheduled</span>;
      default:
        return <span className="badge bg-secondary" style={{ fontSize: '11px' }}>{status}</span>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container-fluid my-4">
      {/* Header with Back Button */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="bg-primary text-white rounded-4 p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-2">Application Leads Management</h2>
                <p className="mb-0">View and manage all job applications received through Agency Bazaar</p>
              </div>
              <Link to="/dashboard" className="btn btn-light btn-lg rounded-pill">
                <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4 g-3">
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body py-2">
              <label className="form-label fw-bold small">🔍 Search</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by name, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body py-2">
              <label className="form-label fw-bold small">📊 Filter by Status</label>
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Interview Scheduled">Interview Scheduled</option>
              </select>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body py-2">
              <label className="form-label fw-bold small">📈 Statistics</label>
              <div className="d-flex justify-content-between align-items-center">
                <span className="small">Total: <strong>{applicants.length}</strong></span>
                <span className="small">Showing: <strong>{filteredApplicants.length}</strong></span>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={fetchApplicants}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-lg rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading applications...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger m-3" role="alert">
              {error}
            </div>
          ) : filteredApplicants.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No applications found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0" style={{ fontSize: '13px' }}>
                <thead className="bg-primary text-white" style={{ fontSize: '13px' }}>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Applicant Name</th>
                    <th>Contact Info</th>
                    <th>Address</th>
                    <th>Profile</th>
                    <th>App. Date</th>
                    <th>Status</th>
                    <th>CV</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplicants.map((applicant, index) => (
                    <tr key={applicant.ApplicantID}>
                      <td>{index + 1}</td>
                      <td>
                        <strong style={{ fontSize: '13px' }}>{applicant.Name}</strong>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          <div>📧 {applicant.Email}</div>
                          <div>📞 {applicant.Contact}</div>
                        </div>
                      </td>
                      <td>
                        <small style={{ fontSize: '12px' }}>
                          {applicant.Address && applicant.Address.length > 40 
                            ? applicant.Address.substring(0, 40) + '...' 
                            : applicant.Address}
                        </small>
                      </td>
                      <td>
                        <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '11px' }}>
                          {applicant.Profile}
                        </span>
                      </td>
                      <td>
                        <small style={{ fontSize: '11px' }}>{formatDate(applicant.ApplicationDate)}</small>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={applicant.Status}
                          onChange={(e) => handleStatusUpdate(applicant.ApplicantID, e.target.value)}
                          style={{ width: '130px', fontSize: '12px' }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Interview Scheduled">Interview Scheduled</option>
                        </select>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleDownloadCV(applicant.ResumePath, applicant.ResumeFileName)}
                        >
                          📄 Download
                        </button>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                            onClick={() => handleViewDetails(applicant)}
                            title="View Details"
                          >
                            👁️
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                            onClick={() => handleDelete(applicant.ApplicantID, applicant.Name)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && selectedApplicant && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Application Details - {selectedApplicant.Name}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <strong>Personal Information</strong>
                      </div>
                      <div className="card-body">
                        <p><strong>Full Name:</strong> {selectedApplicant.Name}</p>
                        <p><strong>Email:</strong> {selectedApplicant.Email}</p>
                        <p><strong>Contact:</strong> {selectedApplicant.Contact}</p>
                        <p><strong>Address:</strong> {selectedApplicant.Address}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <strong>Professional Information</strong>
                      </div>
                      <div className="card-body">
                        <p><strong>Applied For:</strong> {selectedApplicant.Profile}</p>
                        <p><strong>Status:</strong> {getStatusBadge(selectedApplicant.Status)}</p>
                        <p><strong>Application Date:</strong> {formatDate(selectedApplicant.ApplicationDate)}</p>
                        <p><strong>Resume:</strong> 
                          <button 
                            className="btn btn-sm btn-link"
                            onClick={() => handleDownloadCV(selectedApplicant.ResumePath, selectedApplicant.ResumeFileName)}
                          >
                            {selectedApplicant.ResumeFileName}
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={() => {
                    handleStatusUpdate(selectedApplicant.ApplicantID, 'Approved');
                    setShowModal(false);
                  }}
                >
                  Approve Application
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetApplyLeads;


